import React, { useEffect, useState, useContext, memo, useMemo } from "react";
import tinygradient from "tinygradient";
import { Polygon, InfoWindow, MarkerClusterer, Marker } from "@react-google-maps/api";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";
import { mapValueToRange } from "../../../../helpers/fuctionHelpers";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";
import { getPolygonsForIBS, paginatedApiInvoker } from "../../../../utils/webMapApiHelper";
import { getBestOptrIBSMapData, getBestOptrIBSMapDataCluster, getIBSPolygonData, getTooltipData } from "../../../../services/api/mapIBS.api";
import {
  clusterDefaultZoomLvl,
  googleClusterImgUrl,
  LABEL_RENAME,
  SEARCH_AREA_STATES,
  UNIT_MAPPING,
  Names_For_Filter_OPs
} from "../../../../constants/WebMap";
import { SingleBestOpTooltip } from "../../common";

const BestIBSMapElements = ({
  eyeIcon,
  connection_type,
  scoreRangeFrom = 0,
  scoreRangeTo = 100,
  attr,
  aggr,
  names = [],
  types = [],
  apiTrigger,
  gradientAttr = [],
  attrLimits,
  attrTypes,
  isIBSChecked,
  operatorDetails = []
}) => {
  const [ibsPolygons, setIBSPolygons] = useState([]);
  const [polygonData, setPolygonData] = useState([]);
  const [ibsData, setIBSData] = useState([]);
  const [tooltipData, setTooltipData] = useState();
  const [initLoad, setInitLoad] = useState(true);

  const {
    appliedFilters,
    geohashNE,
    geohashSW,
    getBbox,
    mapZoomLevel,
    geohashZoom,
    boundsChanged,
    storeLastDataLoadedBounds,
    setSearchAreaBtn,
    searchAreaBtn,
    setApisOngoing,
    ibsConfig: ibsConfigBasedOnCT,
    mapRef,
    setIBSLegendData,
    setDownloadData,
    downloadData,
  } = useContext(WebMapContext);
  const { countryCode, year, quarter, level, levelVal } = appliedFilters;
  const common_cond = useMemo(() => {
    return geohashZoom && mapZoomLevel >= geohashZoom && connection_type && aggr && attr;
  }, [geohashZoom, mapZoomLevel, connection_type, attr, aggr]);

  const ibsConfig = useMemo(() => {
    return ibsConfigBasedOnCT[connection_type];
  }, [connection_type, ibsConfigBasedOnCT]);


  useEffect(() => {
    resetData();
  }, [countryCode, year, quarter]);

  useEffect(() => {
    const fetchData = async () => {
      if (!ibsData?.length && mapZoomLevel >= clusterDefaultZoomLvl && mapZoomLevel < geohashZoom) {
        const res = await loadIBSMapDataCluster();
        setIBSData([])
        setIBSData(res);
      }
      if (!ibsPolygons?.length && mapZoomLevel >= clusterDefaultZoomLvl && !isIBSChecked) {
        drawOnMap(true);
        storeLastDataLoadedBounds();
      }
    };

    fetchData();
  }, [mapZoomLevel]);

  useEffect(() => {
    const fetchData = async () => {
      if (mapZoomLevel >= clusterDefaultZoomLvl && mapZoomLevel < geohashZoom) {
        const res = await loadIBSMapDataCluster();
        setIBSData([])
        setIBSData(res);
      }
    };

    fetchData();
  }, [attr, connection_type, aggr]);

  useEffect(() => {
    if (geohashZoom && mapZoomLevel < geohashZoom) resetData();
  }, [mapZoomLevel, geohashZoom]);

  useEffect(() => {
    setInitLoad(true);
  }, [year, quarter, mapZoomLevel, geohashZoom, boundsChanged]);

  useEffect(() => {
    if (common_cond) {
      drawOnMap(polygonData.length === 0 || initLoad ? true : false);
      storeLastDataLoadedBounds();
      setInitLoad(false);
    }
  }, [level, levelVal, geohashSW, geohashZoom, boundsChanged, apiTrigger, year, quarter]);

  useEffect(() => {
    if (common_cond && searchAreaBtn === SEARCH_AREA_STATES.FETCH) {
      drawOnMap(true);
      storeLastDataLoadedBounds();
    }
    // eslint-disable-next-line
  }, [searchAreaBtn]);

  useEffect(() => {
    if (tooltipData) {
      setTooltipData();
    }
  }, [level, levelVal, geohashSW, apiTrigger]);

  useEffect(() => {
    if (eyeIcon && ibsConfig && attr) generateIBSLegend();
    else setIBSLegendData([]);
  }, [eyeIcon, ibsConfig, attr, attrLimits, apiTrigger, gradientAttr, year, quarter]);

  const resetData = () => {
    setPolygonData([]);
    setIBSPolygons([]);
    setTooltipData();
  };

  const fitPolygonBounds = (builds) => {
    if (mapZoomLevel < 12 || !builds?.length) return;
    if (mapRef.current) {
      const buildPoints = builds.map(x => x.poly);
      const bounds = new window.google.maps.LatLngBounds();
      buildPoints.forEach((polygon) => {
        polygon.forEach((latLng) => bounds.extend(latLng));
      });
      mapRef.current.fitBounds(bounds);
    }
  };

  const loadPolygonData = async () => {
    setPolygonData([]);
    const { neBound, swBound } = getBbox;
    const northEast = convertArrayToStringWithDelimeter(
      geohashNE.length ? geohashNE : neBound,
      "_"
    );
    const southWest = convertArrayToStringWithDelimeter(
      geohashSW.length ? geohashSW : swBound,
      "_"
    );

    const polygonResponse = await paginatedApiInvoker(
      (pageSize, page) =>
        getIBSPolygonData(
          {
            country: countryCode,
            ...(level && level !== "geoHashLevel" && {
              level:
                level === "adminLevel1"
                  ? "adm1"
                  : level === "adminLevel2"
                    ? "adm2"
                    : "adm3",
            }),
            ...(levelVal && level !== "geoHashLevel" && { levelVal }),
            northEast,
            southWest,
          },
          pageSize,
          page,
          true
        ),
      20000,
      null,
      null,
      null,
      true
    );
    setPolygonData(polygonResponse);
    return polygonResponse;
  };

  const loadIBSMapData = async () => {
    let responseData = [];
    const { neBound, swBound } = getBbox;
    const buildingType = types.length
      ? convertArrayToStringWithDelimeter(types, "_")
      : null;
    const buildingName = names.length
      ? convertArrayToStringWithDelimeter(names, "_")
      : null;
    const northEast = convertArrayToStringWithDelimeter(
      geohashNE.length ? geohashNE : neBound,
      "_"
    );
    const southWest = convertArrayToStringWithDelimeter(
      geohashSW.length ? geohashSW : swBound,
      "_"
    );

    const payload = {
      country: countryCode,
      quarter: `${quarter} ${year}`,
      ...(level &&
        level !== "geoHashLevel" && {
        level:
          level === "adminLevel1"
            ? "adm1"
            : level === "adminLevel2"
              ? "adm2"
              : "adm3",
      }),
      ...(levelVal && level !== "geoHashLevel" && { levelVal }),
      ct: connection_type,
      scoreFrom: scoreRangeFrom,
      scoreTo: scoreRangeTo,
      type: buildingType,
      name: buildingName,
      northEast,
      southWest,
      ...(attr && attr !== "Score" && { attr }),
      ...(aggr && { aggr }),
    };
    await paginatedApiInvoker(
      (pageSize, page) =>
        getBestOptrIBSMapData(payload,
          null,
          pageSize,
          page,
          true
        ),
      20000,
      null,
      (data) => { if (data) responseData = [...responseData, ...data]; },
      (data) => {
        setDownloadData({
          ...downloadData,
          ibsBest: payload,
        });
      },
      true
    );
    if (attr !== "Score" && gradientAttr.includes(attr) && attrLimits) {
      for (const itm of responseData) {
        const norm = mapValueToRange(itm.val, attrLimits.min, attrLimits.max);
        itm.val = norm;
      }
    }
    return responseData;
  }

  const loadIBSMapDataCluster = async () => {
    let responseData = [];
    const { neBound, swBound } = getBbox;
    const buildingType = types.length
      ? convertArrayToStringWithDelimeter(types, "_")
      : null;
    const buildingName = names.length
      ? convertArrayToStringWithDelimeter(names, "_")
      : null;
    const northEast = convertArrayToStringWithDelimeter(
      geohashNE.length ? geohashNE : neBound,
      "_"
    );
    const southWest = convertArrayToStringWithDelimeter(
      geohashSW.length ? geohashSW : swBound,
      "_"
    );
    await paginatedApiInvoker(
      (pageSize, page) =>
        getBestOptrIBSMapDataCluster(
          {
            country: countryCode,
            quarter: `${quarter} ${year}`,
            ...(level && level !== "geoHashLevel" && {
              level:
                level === "adminLevel1"
                  ? "adm1"
                  : level === "adminLevel2"
                    ? "adm2"
                    : "adm3",
            }),
            ...(levelVal && level !== "geoHashLevel" && { levelVal }),
            ct: connection_type,
            scoreFrom: scoreRangeFrom,
            scoreTo: scoreRangeTo,
            type: buildingType,
            name: buildingName,
            northEast,
            southWest,
            ...(attr && attr !== "Score" && { attr }),
            ...(aggr && { aggr })
          },
          pageSize,
          page,
          true
        ),
      20000,
      null,
      (data) => { if (data) responseData = [...responseData, ...data]; },
      null,
      true
    );
    if (attr !== "Score" && gradientAttr.includes(attr) && attrLimits) {
      for (const itm of responseData) {
        const norm = mapValueToRange(itm.val, attrLimits.min, attrLimits.max);
        itm.val = norm;
      }
    }
    return responseData;
  }

  const drawOnMap = async (fetchPolygons) => {
    setIBSPolygons([]);
    setTooltipData();
    let ibsPoly = [];
    setApisOngoing(count => count + 1);
    const res = await loadIBSMapData();
    if (fetchPolygons) {
      ibsPoly = await loadPolygonData();
    } else {
      ibsPoly = polygonData;
    }
    if (!appliedFilters.level) setSearchAreaBtn(SEARCH_AREA_STATES.ENABLED);
    const ibsDataWithPoligons = getPolygonsForIBS(res, ibsPoly);
    if (names.length) fitPolygonBounds(ibsDataWithPoligons);
    setIBSPolygons(ibsDataWithPoligons);
    setApisOngoing(count => count - 1);
  };

  const colorsSection = useMemo(() => {
    if (ibsConfig) {
      const section = ibsConfig?.[0]?.content.find(x => x.type === attr);
      return section ? section.values : ibsConfig?.[0]?.content[0].values;
    }
    return null;
  }, [ibsConfig, attr]);

  const gradient = useMemo(() => {
    if (gradientAttr?.includes(attr) && colorsSection && "colorGradient" in colorsSection[0]) {
      return tinygradient(colorsSection[0].colorGradient);
    }
    return tinygradient([
      { color: '#B3B3B3', pos: 0 },
      { color: '#E8D166', pos: 0.5 },
      { color: '#D64550', pos: 1 }
    ]);
  }, [colorsSection, attr, gradientAttr]);

  const getPolygonColor = (ibsData) => {
    if (gradientAttr.includes(attr)) {
      const colorObj = operatorDetails?.find(op => op.operator === ibsData.ibs.best_op && op.isAnonymized === false);
      return colorObj?.color || '#B4B4B4';
    } else {
      if (attr === "Score") return gradient?.rgbAt(Number(ibsData.ibs.score));
      else if (ibsData.ibs.val >= 0 && ibsData.ibs.val <= 1) return gradient?.rgbAt(Number(ibsData.ibs.val));
    }
    return null;
  };

  const onClickBuild = async (id, op) => {
    setTooltipData();
    try {
      const { data, anomData } = await getTooltipData(
        {
          country: countryCode,
          quarter: `${quarter} ${year}`,
          operator: op,
          ct: connection_type,
          id,
          ...(attr !== "Score" && { attr }),
          aggr
        });
      if (data) {
        const operatorData = operatorDetails?.find(x => x.operator === data?.best_op);
        if (operatorData?.isAnonymized) {
          const anomOp = anomData?.find(x => x.operator === operatorData?.operator);
          operatorData.name = anomOp?.anonymizedName || ''
        }
        setTooltipData({
          operator: data?.best_op,
          opIcon: operatorData?.icon,
          operatorName: operatorData.name,
          isAnonymized: operatorData?.isAnonymized,
          body: {
            "OSM Id": id,
            "Building Type": data.type,
            Latitude: operatorData?.isAnonymized
              ? data.geoPoint.coordinates[1]
                ?.toFixed(5)
                ?.replace(/^\d+/, (match) => 'x'.repeat(match.length))
                ?.replace(/\.\d+/, (match) => `.${'x'.repeat(match.length - 1)}`)
              : data.geoPoint.coordinates[1]?.toFixed(5),
            Longitude: operatorData?.isAnonymized
              ? data.geoPoint.coordinates[0]
                ?.toFixed(5)
                ?.replace(/^\d+/, (match) => 'x'.repeat(match.length))
                ?.replace(/\.\d+/, (match) => `.${'x'.repeat(match.length - 1)}`)
              : data.geoPoint.coordinates[0]?.toFixed(5),
            Area: parseFloat(data.area)?.toFixed(3) + " m",
            "Total Devices": data.tot_d?.toLocaleString("en-US"),
            "Total Samples": data.tot_r?.toLocaleString("en-US"),
            Score: data.f_score,
            ...(attr !== "Score" && {
              ...(aggr !== "SUM" && { "Aggregation Type": data.aggr }),
              [`${LABEL_RENAME[data.attr] ? LABEL_RENAME[data.attr] : data.attr}`]: `${data.val.toLocaleString("en-US")}${UNIT_MAPPING[data.attr] && data.val !== "No Data" ? ` ${UNIT_MAPPING[data.attr]}` : ''}` // Appending unit if exists
            }),
            "Service Level": data.s_lvl,
          },
          point: { lat: data.geoPoint.coordinates[1], lng: data.geoPoint.coordinates[0] },
          buildingName: data.name,
        })

      }
    } catch (e) {
      console.log('Error getting IBS tooltip: ', e)
    }
  };

  const generateIBSLegend = async () => {
    const section = ibsConfig?.[0]?.content.find(x => x.type === attr) || ibsConfig?.[0]?.content[0];
      const notPermitted = {
        isNotPermitted: true,
        text: 'Not Permitted'
      }
      setIBSLegendData([
        {
          title: 'Operators',
          values: operatorDetails?.filter(op => op.type === "MNO" && op.isAnonymized === false).map((op) => ({
            markerType: "colorBox",
            markerColor: op.color,
            img: op.icon,
          })),
          notPermitted
        },
      ]);
  };

  const getPolygonCentroid = (data) => {
    return { lng: data[0], lat: data[1] };
  };

  return (
    <>
      {ibsPolygons?.length > 0 && mapZoomLevel >= geohashZoom ? (
        ibsPolygons.map((data, index) => {
          const color = getPolygonColor(data);
          return color ? (
            <div className="data-on-map">
              <Polygon
                key={index}
                path={data.poly}
                options={{
                  fillColor: color,
                  fillOpacity: 1,
                  strokeColor: color,
                  strokeOpacity: 1,
                  strokeWeight: mapZoomLevel > 15 ? 1.8 : mapZoomLevel > 14 ? 1.4 : 0.8,
                  clickable: true,
                  draggable: false,
                  editable: false,
                  geodesic: false,
                  zIndex: 1,
                }}
                onClick={() => {
                  onClickBuild(data.ibs.id, data.ibs.op);
                }}
              />
            </div>
          ) : <></>;
        })
      ) : ibsPolygons?.length === 0 && mapZoomLevel >= geohashZoom ? (
        <></>
      ) : ibsPolygons?.length === 0 && ibsData?.length && mapZoomLevel >= clusterDefaultZoomLvl ? (
        <MarkerClusterer options={{ imagePath: googleClusterImgUrl }}>
          {(clusterer) =>
            ibsData.map((data, index) => {
              const centroid = getPolygonCentroid(data.geoPoint.coordinates);

              return centroid ? (
                <Marker
                  key={index}
                  position={{ lat: centroid.lat, lng: centroid.lng }}
                  clusterer={clusterer}
                  options={{
                    clickable: false
                  }}
                  onLoad={(marker) => {
                    if (marker) {
                      marker.setVisible(false);
                    }
                  }}
                />
              ) : <></>;
            })
          }
        </MarkerClusterer>
      ) : <></>}

      {tooltipData && mapZoomLevel >= geohashZoom && ibsPolygons.length && (
        <InfoWindow
          position={tooltipData.point}
          onCloseClick={() => {
            setTooltipData();
          }}
          options={{ disableAutoPan: true }}
        >
          <SingleBestOpTooltip data={tooltipData} attr={attr} attrTypes={attrTypes} />
        </InfoWindow>
      )}
    </>

  );
};

export default memo(BestIBSMapElements);
